










































































































import {
  computed,
  ref,
  defineComponent,
  watch,
  vue3Set
} from '~/utils/nuxt3-migration'
import { useNamespacedStore } from '~/compositions/store'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import { defineComponentTranslations } from '~/utils/i18n'
import CStepper from '~/components/shared/configurable/stepper/CStepper.vue'
import { ciTimes } from '~/icons/source/solid/times'
import { SIMPLE_DATETIME_FORMAT } from '~/constants/date'
import { format } from '~/utils/date'
import CCard from '~/components/shared/configurable/card/CCard.vue'
import {
  AuditActingUser,
  AuditRequest,
  AuditRequestStatusStatus
} from '~/models/audit/request'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import PayAuditButton from '~/components/shared/audits/request/timeline/PayAuditButton.vue'
import AuditProgressButtons from '~/components/shared/classified/audits/AuditProgressButtons.vue'

export default defineComponent({
  components: {
    AuditProgressButtons,
    PayAuditButton,
    CStepper,
    CCard
  },
  setup() {
    const { state, getters } = useNamespacedStore(CLASSIFIED_VIEW_NS)
    const userStore = useNamespacedStore<UserState>(USER_NS)
    const { getters: userGetters } = userStore

    const stepStatus = ref([])
    const selectedStep = ref([])
    const isProgressExpanded = ref(false)

    const auditRequests = computed(() => state.auditRequests)
    const filteredAuditRequests = ref(getFilteredAuditRequests())
    const isClassifiedOwner = computed(() => getters('isOwn'))
    const userIds = computed(() => userGetters('userIds'))

    watch(auditRequests, newVal => {
      if (newVal) {
        filteredAuditRequests.value = getFilteredAuditRequests()
        auditsRequestInit()
      }
    })

    const hasMoreThanTwoRequests = computed(
      () => filteredAuditRequests.value.length > 2
    )

    function getFilteredAuditRequests() {
      return (
        auditRequests.value?.filter(
          ar =>
            !(
              ar?.currentStatus?.status ===
                AuditRequestStatusStatus.PENDING_ACCEPTANCE &&
              isClassifiedOwner.value
            )
        ) || []
      )
    }

    const requestsProgress = computed(() => {
      const requests = filteredAuditRequests.value
        ? [...filteredAuditRequests.value]
        : []

      const visibleRequests = isProgressExpanded.value
        ? requests
        : requests.slice(0, 2)

      return visibleRequests.map((auditRequest, i) => {
        const statusSummary = auditRequest?.statusSummary
        const steps = []
        const isPendingPayment = !!statusSummary.find(
          summary => summary.isActive && summary.status === 'payment'
        )
        const numberOfSteps = auditRequest.statusSummary.length - 1
        const isAuditsCancelled = statusSummary.some(sm => sm.cancelled)
        statusSummary.forEach((summary, j) => {
          const isSelected = selectedStep.value[i] === j
          const isCompleted = summary.passed || summary.isActive
          const isCancelled = summary.cancelled
          steps.push({
            completed: isCompleted,
            requestId: auditRequest.id,
            label: summary.name,
            statusHistory: statusSummary,
            icon: isCancelled ? ciTimes : null,
            stepClasses: stepClasses(
              isCompleted,
              isSelected,
              isCancelled,
              j,
              numberOfSteps,
              selectedStep.value[i],
              summary.isActive
            ),
            disabled: !isCompleted && !summary.isActive
          })
        })
        return {
          ...auditRequest,
          steps,
          isPendingPayment,
          isAuditsCancelled,
          requestId: auditRequest.id,
          payment:
            isClassifiedOwner.value && !auditRequest.isSelfRequestedAudit
              ? null
              : auditRequest.payment,
          isUserWhoRequestedIt: userIds.value.includes(
            auditRequest.requestedByUser.id
          )
        }
      })
    })

    function handleStepChange(index, step) {
      vue3Set(
        stepStatus.value,
        index,
        getStepInfo(filteredAuditRequests.value[index], step)
      )
      vue3Set(selectedStep.value, index, step)
    }

    function auditsRequestInit() {
      filteredAuditRequests.value.forEach((ar, i) => {
        const selected = ar.statusSummary.findIndex(summary => summary.isActive)
        vue3Set(selectedStep.value, i, selected)
        stepStatus.value[i] = getStepInfo(ar, selected)
      })
    }

    function getStepInfo(auditRequests: AuditRequest, stepIndex: number) {
      const { statusSummary, requestedByUser } = auditRequests
      return {
        title: statusSummary[stepIndex]?.description,
        date: format(statusSummary[stepIndex]?.created, SIMPLE_DATETIME_FORMAT),
        cancelled: statusSummary[stepIndex]?.cancelled,
        username: requestedByUser.displayName,
        status: statusSummary[stepIndex].status
      }
    }

    function labelClasses(
      isCompleted: boolean,
      isSelected: boolean,
      isCancelled: boolean
    ) {
      if (isCancelled && isCompleted) {
        return '[&_.step-label-horizontal]:tw-text-red-500 tw-tw-text-white'
      } else if (isSelected) {
        return '[&_.step-label-horizontal]:tw-text-blue-500 tw-tw-text-white'
      } else if (isCompleted) {
        return '[&_.step-label-horizontal]:tw-text-green-500 tw-tw-text-white'
      }
    }

    function stepClasses(
      isCompleted: boolean,
      isSelected: boolean,
      isCancelled: boolean = false,
      index: number,
      numberOfSteps: number,
      currentStep: number,
      isActive: boolean
    ) {
      const isLastStep = numberOfSteps === index
      const classes = []

      if (isCancelled && isActive) {
        classes.push(
          '[&_.c-step-dot]:!tw-bg-red-500 [&_.c-step-dot]:tw-border-red-600 [&_.step-line-up]:tw-bg-red-500'
        )
      } else if (isSelected) {
        classes.push('[&_.c-step-dot]:tw-bg-blue-500')
        if (
          isLastStep ||
          currentStep < index ||
          (currentStep === index && !isActive)
        ) {
          classes.push('[&_.c-step-line-after]:tw-bg-blue-500')
        }
      } else if (isCompleted) {
        classes.push(
          '[&_.c-step-dot]:!tw-bg-green-500 [&_.c-step-dot]:tw-border-green-600 [&_.step-line-up]:tw-bg-green-500'
        )
        if (isLastStep || !isActive) {
          classes.push('[&_.c-step-line-after]:tw-bg-green-500')
        }
      }

      classes.push(
        'max-sm:[&_.step-label-horizontal]:tw-hidden sm:[&_.step-label-horizontal]:tw-inline-block md:[&_.step-label-horizontal]:tw-hidden lg:[&_.step-label-horizontal]:tw-inline-block'
      )
      classes.push('[&_.c-step-dot]:tw-text-white')
      classes.push(labelClasses(isCompleted, isSelected, isCancelled))
      return classes
    }

    function updatedAuditRequest(updatedRequest: AuditRequest, index: number) {
      vue3Set(filteredAuditRequests.value, index, updatedRequest)
      auditsRequestInit()
    }

    function handleStatusChange(
      status: AuditRequestStatusStatus,
      index: number
    ) {
      if (filteredAuditRequests.value[index]?.currentStatus) {
        filteredAuditRequests.value[index].currentStatus.status = status
        auditsRequestInit()
      }
    }

    return {
      auditRequests,
      handleStepChange,
      requestsProgress,
      stepStatus,
      selectedStep,
      isClassifiedOwner,
      getters,
      isProgressExpanded,
      AuditRequestStatusStatus,
      hasMoreThanTwoRequests,
      handleStatusChange,
      updatedAuditRequest,
      AuditActingUser
    }
  },
  i18n: defineComponentTranslations({
    audit_progress: {
      en:
        'Technical Audit Request Progress | Technical Audit Requests Progress',
      el:
        'Πορεία αιτήματος Τεχνικού Ελέγχου | Πορεία αιτημάτων Τεχνικού Ελέγχου'
    },
    technical_inspection_payment: {
      en: 'Technical inspection payment',
      el: 'Πληρωμή τεχνικού ελέγχου'
    },
    appointment_made_for: {
      en: '{blue} for {date}',
      el: '{blue} για τις {date}'
    },
    appointment_made_with_owner_blue: {
      en: 'An appointment with the owner was made',
      el: 'Κλείστηκε ραντεβού με τον ιδιοκτήτη'
    },
    appointment_made_blue: {
      en: 'You made an appointment',
      el: 'Κλείσατε ραντεβού'
    }
  })
})
